// InsightCard/InsightPreviewCards.tsx
// CHANGES from original:
//   1. Added `onDataPointClick?: (column: string, value: any) => void` to PreviewProps
//   2. TrendAnalysisPreview  — circle click fires onDataPointClick with dimension column + period value
//   3. HistogramPreview      — bar click fires with metric column + bin range label
//   4. TreemapPreview        — cell click fires with dimension column + label value
//   5. HeatmapPreview        — cell click fires with dimension column + row label
//   6. WordCloudPreview      — word click fires with selected_columns[0] + word text
//   7. All other previews unchanged (AnomalyPreview, CriticalMetricPreview, etc. are not
//      easily clickable or don't carry a dimension to filter on)

import React from "react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type PreviewProps = {
  card?: any;
  onDataPointClick?: (column: string, value: any) => void;
};

type WrapperProps = {
  title: string;
  children: React.ReactNode;
};

const getCardRows = (data: any) => {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

const getNumericValue = (value: any, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

// ─── Shared cursor style for clickable data points ───────────────────────────
const clickableStyle: React.CSSProperties = {
  cursor: "pointer",
  transition: "opacity 0.15s",
};

export const PreviewCardWrapper: React.FC<WrapperProps> = ({
  title,
  children,
}) => (
  <div style={{ width: "100%" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontWeight: 700,
        fontSize: 15,
        marginBottom: 12,
        color: "#1f1b3d",
      }}
    >
      <span>{title}</span>
    </div>
    {children}
  </div>
);

// ─── CriticalMetricPreview (no click — single aggregate value) ────────────────
export const CriticalMetricPreview: React.FC<PreviewProps> = ({ card }) => {
  const { t } = useTranslation("dashboardModals");
  const value = getNumericValue(
    card?.data?.current_value ?? card?.data?.value ?? 76,
  );
  const change =
    card?.data?.change_percent !== undefined
      ? Number(card.data.change_percent)
      : 4.5;
  const target = card?.data?.target ?? 90;

  return (
    <PreviewCardWrapper
      title={card?.title || t("insightPreviewCards.criticalMetricSnapshot")}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 52, fontWeight: 700, color: "#1f1b3d" }}>
            {value.toFixed(0)}
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: change >= 0 ? "#0f9f55" : "#dc2626",
            }}
          >
            {change > 0 ? "+" : ""}
            {change.toFixed(1).replace(".", ",")}% {change >= 0 ? "↗" : "↘"}
          </div>
          <Text type="secondary" style={{ fontSize: 16 }}>
            {t("insightPreviewCards.targetLabel")} <b>{target}</b>
          </Text>
        </div>
        <svg width="180" height="130" viewBox="0 0 180 130">
          <path
            d="M5 115 C25 105, 22 95, 35 90 C48 82, 42 60, 58 65 C75 72, 72 10, 92 15 C110 20, 100 62, 120 58 C140 52, 132 90, 150 84 C165 78, 162 70, 175 50"
            fill="none"
            stroke="#0f9f55"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </PreviewCardWrapper>
  );
};

// ─── ProgressPreview (no click) ───────────────────────────────────────────────
export const ProgressPreview: React.FC<PreviewProps> = ({ card }) => {
  const { t } = useTranslation("dashboardModals");
  const rows = getCardRows(card?.data);
  const metricKey = card?.metrics?.[0]?.column;

  let percent = 80;
  if (metricKey && rows.length) {
    const values = rows.map((row: any) => getNumericValue(row[metricKey], 0));
    const avg =
      values.reduce((sum: number, v: number) => sum + v, 0) / values.length;
    percent = avg >= 0 && avg <= 1 ? Math.round(avg * 100) : Math.round(avg);
  }

  const safePercent = Math.max(0, Math.min(100, percent));
  const completed = Math.round(safePercent / 10);

  return (
    <PreviewCardWrapper title={card?.title || t("insightPreviewCards.progress")}>
      <div
        style={{
          textAlign: "center",
          fontSize: 32,
          fontWeight: 800,
          color: "#0f9f55",
          marginBottom: 28,
        }}
      >
        {t("insightPreviewCards.completedProgress", { completed })}
      </div>
      <div
        style={{
          display: "flex",
          height: 14,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div style={{ width: `${safePercent}%`, background: "#9b6fe8" }} />
        <div style={{ flex: 1, background: "#e5e5e5" }} />
      </div>
    </PreviewCardWrapper>
  );
};

// ─── TrendAnalysisPreview — CLICKABLE circles ────────────────────────────────
// Clicking a data point filters by the dimension column (e.g. "month") = period value
export const TrendAnalysisPreview: React.FC<PreviewProps> = ({
  card,
  onDataPointClick,
}) => {
  const { t } = useTranslation("dashboardModals");
  const rows = getCardRows(card?.data);
  const dimensionColumn = card?.dimensions?.[0]?.column;

  // Build typed data: { value, period }
  const data: { value: number; period: string }[] = rows.length
    ? rows.slice(0, 8).map((row: any) => ({
        value: getNumericValue(row.value ?? row.count ?? row.total, 0),
        period: String(row.period ?? row[dimensionColumn] ?? ""),
      }))
    : [
        { value: 10, period: "Jan" },
        { value: 55, period: "Feb" },
        { value: 70, period: "Mar" },
        { value: 40, period: "Apr" },
        { value: 140, period: "May" },
        { value: 185, period: "Jun" },
        { value: 145, period: "Jul" },
        { value: 180, period: "Aug" },
      ];

  const values = data.map((d) => d.value);
  const max = Math.max(...values, 1);

  const points = data
    .map((_, index) => {
      const x = 20 + index * (360 / Math.max(data.length - 1, 1));
      const y = 130 - (data[index].value / max) * 95;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <PreviewCardWrapper
      title={card?.title || t("insightPreviewCards.trendAnalysis")}
    >
      <svg width="100%" height="150" viewBox="0 0 420 150">
        <polyline
          fill="none"
          stroke="#A072EB"
          strokeWidth="3"
          points={points}
        />
        {data.map((d, index) => {
          const x = 20 + index * (360 / Math.max(data.length - 1, 1));
          const y = 130 - (d.value / max) * 95;
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="6"
              fill="#fff"
              stroke="#A072EB"
              strokeWidth="2"
              style={onDataPointClick ? clickableStyle : undefined}
              onClick={() => {
                if (!onDataPointClick || !dimensionColumn || !d.period) return;
                onDataPointClick(dimensionColumn, d.period);
              }}
              onMouseEnter={(e) => {
                if (onDataPointClick) e.currentTarget.setAttribute("r", "8");
              }}
              onMouseLeave={(e) => {
                if (onDataPointClick) e.currentTarget.setAttribute("r", "6");
              }}
            />
          );
        })}
      </svg>
    </PreviewCardWrapper>
  );
};

// ─── AnomalyPreview (static SVG — no meaningful click target) ────────────────
export const AnomalyPreview: React.FC<PreviewProps> = ({ card }) => {
  const { t } = useTranslation("dashboardModals");
  return (
    <PreviewCardWrapper
      title={card?.title || t("insightPreviewCards.anomalyDetectionTitle")}
    >
      <svg width="100%" height="150" viewBox="0 0 420 150">
        <path
          d="M0 95 C30 80, 35 50, 60 70 C90 95, 105 115, 130 85 C160 50, 180 40, 205 62 C230 84, 250 45, 280 58 C310 72, 330 78, 350 92 C370 105, 390 60, 420 70"
          fill="none"
          stroke="#A072EB"
          strokeWidth="3"
        />
        <path
          d="M0 95 C30 80, 35 50, 60 70 C90 95, 105 115, 130 85 C160 50, 180 40, 205 62 C230 84, 250 45, 280 58 C310 72, 330 78, 350 92 C370 105, 390 60, 420 70 L420 140 L0 140 Z"
          fill="rgba(160,114,235,0.16)"
        />
        <rect x="315" y="35" width="78" height="34" rx="6" fill="#ff4d6d" />
        <text x="354" y="50" textAnchor="middle" fontSize="10" fill="white">
          {t("insightPreviewCards.anomalyLabel")}
        </text>
        <text x="354" y="62" textAnchor="middle" fontSize="10" fill="white">
          {t("insightPreviewCards.detectedLabel")}
        </text>
        <circle cx="397" cy="52" r="5" fill="#ff4d6d" />
      </svg>
    </PreviewCardWrapper>
  );
};

// ─── HistogramPreview — CLICKABLE bars ───────────────────────────────────────
// Clicking a bar filters by metric column = bin range label (e.g. "95.0-140.0")
export const HistogramPreview: React.FC<PreviewProps> = ({
  card,
  onDataPointClick,
}) => {
  const { t } = useTranslation("dashboardModals");
  const bins = Array.isArray(card?.data?.bins) ? card.data.bins : [];
  const metricColumn = card?.metrics?.[0]?.column;

  const bars: { count: number; range: string }[] = bins.length
    ? bins.map((bin: any) => ({
        count: getNumericValue(bin.count, 0),
        range: String(bin.range ?? ""),
      }))
    : [
        45, 55, 70, 92, 118, 130, 126, 112, 100, 88, 76, 64, 58, 70, 90, 142,
        170, 160,
      ].map((count, i) => ({ count, range: `Bin ${i + 1}` }));

  const max = Math.max(...bars.map((b) => b.count), 1);

  return (
    <PreviewCardWrapper title={card?.title || t("insightPreviewCards.histogram")}>
      <div
        style={{
          height: 155,
          display: "flex",
          alignItems: "flex-end",
          gap: 4,
          background: "#f8fbff",
          padding: "12px 8px 8px",
          borderRadius: 8,
        }}
      >
        {bars.map((bar, index) => (
          <div
            key={index}
            title={bar.range ? `${bar.range}: ${bar.count}` : undefined}
            onClick={() => {
              if (!onDataPointClick || !metricColumn || !bar.range) return;
              onDataPointClick(metricColumn, bar.range);
            }}
            style={{
              flex: 1,
              height: `${Math.max(8, (bar.count / max) * 130)}px`,
              background: "#A072EB",
              borderRadius: "4px 4px 0 0",
              ...(onDataPointClick
                ? {
                    ...clickableStyle,
                    opacity: 0.85,
                  }
                : {}),
            }}
            onMouseEnter={(e) => {
              if (onDataPointClick)
                (e.currentTarget as HTMLDivElement).style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              if (onDataPointClick)
                (e.currentTarget as HTMLDivElement).style.opacity = "0.85";
            }}
          />
        ))}
      </div>
    </PreviewCardWrapper>
  );
};

// ─── TreemapPreview — CLICKABLE cells ────────────────────────────────────────
// Clicking a cell filters by dimension column = category label
export const TreemapPreview: React.FC<PreviewProps> = ({
  card,
  onDataPointClick,
}) => {
  const { t } = useTranslation("dashboardModals");
  const rows = getCardRows(card?.data);
  const dimensionKey = card?.dimensions?.[0]?.column;
  const metricKey = card?.metrics?.[0]?.column;

  let items: { label: string; value: number }[] = [];

  if (dimensionKey && metricKey && rows.length) {
    const grouped = rows.reduce((acc: Record<string, number>, row: any) => {
      const label = String(row[dimensionKey] ?? "Unknown");
      acc[label] = (acc[label] || 0) + getNumericValue(row[metricKey], 0);
      return acc;
    }, {});

    items = (Object.entries(grouped) as [string, number][])
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }

  if (!items.length) {
    items = [
      { label: "Category A", value: 30 },
      { label: "Category B", value: 25 },
      { label: "Category C", value: 20 },
    ];
  }

  return (
    <PreviewCardWrapper title={card?.title || t("insightPreviewCards.treemap")}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 0.8fr 1.2fr 1.2fr",
          gridTemplateRows: "60px 60px",
          gap: 3,
          height: 125,
        }}
      >
        {items.map((item, index) => (
          <div
            key={`${item.label}-${index}`}
            title={`${item.label}: ${item.value}`}
            onClick={() => {
              if (!onDataPointClick || !dimensionKey) return;
              onDataPointClick(dimensionKey, item.label);
            }}
            style={{
              background: index % 3 === 0 ? "#9b6fe8" : "#3f8ff0",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              borderRadius: 2,
              padding: 4,
              textAlign: "center",
              overflow: "hidden",
              lineHeight: 1.3,
              wordBreak: "break-word",
              ...(onDataPointClick
                ? {
                    ...clickableStyle,
                    opacity: 0.9,
                  }
                : {}),
            }}
            onMouseEnter={(e) => {
              if (onDataPointClick)
                (e.currentTarget as HTMLDivElement).style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              if (onDataPointClick)
                (e.currentTarget as HTMLDivElement).style.opacity = "0.9";
            }}
          >
            {item.label}
          </div>
        ))}
      </div>
    </PreviewCardWrapper>
  );
};

// ─── HeatmapPreview — CLICKABLE rows (dimension value) ───────────────────────
// Clicking a row label or any cell in that row filters by dimension column = row label
export const HeatmapPreview: React.FC<PreviewProps> = ({
  card,
  onDataPointClick,
}) => {
  const { t } = useTranslation("dashboardModals");
  const rowsData = getCardRows(card?.data);
  const dimensionKey = card?.dimensions?.[0]?.column;

  const labels: string[] =
    dimensionKey && rowsData.length
      ? rowsData
          .slice(0, 7)
          .map((row: any) => String(row[dimensionKey] ?? "N/A"))
      : ["Wk 1", "Wk 2", "Wk 3", "Wk 4", "Wk 5", "Wk 6", "Wk 7"];

  const cols = ["A", "B", "C", "D", "E", "F", "G", "H"];
  const colors = ["#f1d9ff", "#c99af4", "#9b6fe8", "#4A1C96"];

  return (
    <PreviewCardWrapper title={card?.title || t("insightPreviewCards.heatmap")}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          {/* Column headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `42px repeat(${cols.length}, 1fr)`,
              gap: 4,
              fontSize: 11,
              color: "#777",
              marginBottom: 5,
            }}
          >
            <span />
            {cols.map((col) => (
              <span key={col} style={{ textAlign: "center" }}>
                {col}
              </span>
            ))}
          </div>

          {/* Rows */}
          {labels.map((row: string, rowIndex: number) => (
            <div
              key={`${row}-${rowIndex}`}
              style={{
                display: "grid",
                gridTemplateColumns: `42px repeat(${cols.length}, 1fr)`,
                gap: 4,
                marginBottom: 4,
                ...(onDataPointClick ? clickableStyle : {}),
              }}
              onClick={() => {
                if (!onDataPointClick || !dimensionKey) return;
                // Use the real row value from data if available
                const realValue = rowsData[rowIndex]?.[dimensionKey] ?? row;
                onDataPointClick(dimensionKey, realValue);
              }}
              title={
                onDataPointClick
                  ? t("insightPreviewCards.filterByValue", { value: row })
                  : undefined
              }
            >
              <span
                style={{
                  fontSize: 11,
                  color: "#777",
                  fontWeight: onDataPointClick ? 600 : 400,
                }}
              >
                {row.slice(0, 5)}
              </span>
              {cols.map((col, colIndex) => (
                <div
                  key={`${row}-${col}`}
                  style={{
                    height: 18,
                    borderRadius: 9,
                    background:
                      colors[(rowIndex + colIndex * 2) % colors.length],
                  }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div
          style={{
            width: 30,
            fontSize: 10,
            color: "#777",
            textAlign: "center",
          }}
        >
          <div>{t("insightPreviewCards.high")}</div>
          <div
            style={{
              width: 14,
              height: 94,
              margin: "4px auto",
              borderRadius: 8,
              background: "linear-gradient(#4A1C96, #f1d9ff)",
            }}
          />
          <div>{t("insightPreviewCards.low")}</div>
        </div>
      </div>
    </PreviewCardWrapper>
  );
};

// ─── WordCloudPreview — CLICKABLE words ──────────────────────────────────────
// Clicking a word filters by selected_columns[0] contains that word
export const WordCloudPreview: React.FC<PreviewProps> = ({
  card,
  onDataPointClick,
}) => {
  const { t } = useTranslation("dashboardModals");
  const realWords = Array.isArray(card?.data?.words) ? card.data.words : [];
  const textColumn = card?.selected_columns?.[0];

  const words = realWords.length
    ? realWords.slice(0, 18).map((w: any) => ({
        text: String(w.text ?? w.word ?? "Word"),
        count: getNumericValue(w.count, 1),
      }))
    : [
        { text: "Survey", count: 10 },
        { text: "Service", count: 6 },
        { text: "Support", count: 5 },
      ];

  const max = Math.max(...words.map((w: any) => w.count), 1);

  return (
    <PreviewCardWrapper title={card?.title || t("insightPreviewCards.wordCloud")}>
      <div
        style={{
          height: 150,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px 12px",
          overflow: "hidden",
          padding: "8px 18px",
        }}
      >
        {words.map((word: any, index: number) => {
          const size = 12 + Math.round((word.count / max) * 16);
          return (
            <span
              key={`${word.text}-${index}`}
              title={
                onDataPointClick
                  ? t("insightPreviewCards.filterByQuotedValue", {
                      value: word.text,
                    })
                  : undefined
              }
              onClick={() => {
                if (!onDataPointClick || !textColumn) return;
                // Use "contains" filter for text columns
                onDataPointClick(`__contains__${textColumn}`, word.text);
              }}
              style={{
                fontSize: size,
                fontWeight: 700,
                lineHeight: 1,
                color: index % 3 === 0 ? "#A072EB" : "#4A1C96",
                maxWidth: 140,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...(onDataPointClick ? clickableStyle : {}),
              }}
              onMouseEnter={(e) => {
                if (onDataPointClick)
                  (e.currentTarget as HTMLSpanElement).style.opacity = "0.7";
              }}
              onMouseLeave={(e) => {
                if (onDataPointClick)
                  (e.currentTarget as HTMLSpanElement).style.opacity = "1";
              }}
            >
              {word.text}
            </span>
          );
        })}
      </div>
    </PreviewCardWrapper>
  );
};

// ─── GeoClusterPreview (static — lat/lng clusters, no meaningful click column) ─
export const GeoClusterPreview: React.FC<PreviewProps> = ({ card }) => {
  const { t } = useTranslation("dashboardModals");
  return (
    <PreviewCardWrapper
      title={card?.title || t("insightPreviewCards.geoClusterMap")}
    >
      <div
        style={{
          height: 150,
          borderRadius: 8,
          background:
            "linear-gradient(135deg, #cfcfcf 0%, #b9b9b9 45%, #d8d8d8 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {[8, 20, 28, 48, 65, 78, 88, 94].map((left, index) => (
          <div
            key={index}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: `${30 + ((index * 17) % 50)}%`,
              width: index % 2 ? 20 : 28,
              height: index % 2 ? 20 : 28,
              borderRadius: "50%",
              background: index % 2 === 0 ? "#4A1C96" : "#A072EB",
              color: "#fff",
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "translate(-50%, -50%)",
              fontWeight: 700,
            }}
          >
            {index % 3 === 0 ? "1" : "2"}
          </div>
        ))}
      </div>
    </PreviewCardWrapper>
  );
};

// ─── LocationMapPreview (static) ─────────────────────────────────────────────
export const LocationMapPreview: React.FC<PreviewProps> = ({ card }) => {
  const { t } = useTranslation("dashboardModals");
  return (
  <PreviewCardWrapper
    title={card?.title || t("insightPreviewCards.locationMap")}
  >
    <div
      style={{
        height: 150,
        borderRadius: 8,
        background:
          "linear-gradient(135deg, #f2f2f2 0%, #e5e5e5 45%, #f8f8f8 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "38%",
          top: "50%",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: "#A072EB",
          boxShadow: "0 0 0 6px rgba(160,114,235,0.22)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 10,
          bottom: 10,
          color: "#4A1C96",
        }}
      >
        +<br />−
      </div>
    </div>
  </PreviewCardWrapper>
  );
};
